import React, { useState, useEffect } from "react";
import axiosClient from "../api/axiosClient";
import { logoutUser } from "../api/tokenUtils";
import { useNavigate } from 'react-router-dom';
import { FaClock } from 'react-icons/fa';
import "../Register.css";

const Dashboard = () => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [isClockedIn, setIsClockedIn] = useState(false); // Track clock-in status
  const [clockInTime, setClockInTime] = useState(false);
  const [clockOutTime, setClockOutTime] = useState(false);
  const [address, setAddress] = useState(""); // Holds the reverse-geocoded address
  const [loading, setLoading] = useState(false);
  
  // State to manage showing the confirmation modal and pending location info
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [pendingLocation, setLocation] = useState({
    latitude: null,
    longitude: null,
    address: "",
  });

  const formatAddress = (data) => {
    if (data.address) {
      const { house_number = "", road = "", suburb, city_district, city, state = "", postcode = "" } = data.address;
      let shortAddr = `${house_number} ${road}`.trim();
      const locality = suburb || city_district || city || "";
      if (locality)    shortAddr += `, ${locality}`;
      if (state)       shortAddr += `, ${state}`;
      if (postcode)    shortAddr += `, ${postcode}`;
      return shortAddr;
    }
    if (data.display_name) {
      return data.display_name;
    }
    return "Address not found";
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem("access_token");
        if (!token) {
          navigate("/"); // Redirect to login if no token
          return;
        }
        const response = await axiosClient.get("/user/me/");
        setUser(response.data);
      } catch (error) {
        console.error("Error fetching user:", error);
        navigate("/"); // Redirect to login on error
      }
    };

    const checkClockStatus = async () => {
      try {
        const response = await axiosClient.get("/user/me/");
        setIsClockedIn(response.data.is_clocked_in);

        // If the user is clocked in, fetch the clock-in time
        if (response.data.is_clocked_in) {
          await fetchClockInTime();
        } else {
          await fetchFullShiftTime();
        }
      } catch (error) {
        console.error("Error fetching shift status:", error);
      }
    };

    const fetchClockInTime = async () => {
      try {
        const response = await axiosClient.get("/shifts/status/");
        if (response.data.status === "success") {
          setClockInTime(response.data.clock_in_time);
          setAddress(response.data.address_clock_in_str);
        }
      } catch (error) {
        console.error("Error fetching shift status:", error);
      }
    };

    const fetchFullShiftTime = async () => {
      try {
        const response = await axiosClient.get("/shifts/full_shift/");
        if (response.data.status === "success") {
          setClockInTime(response.data.clock_in_time);
          setClockOutTime(response.data.clock_out_time);
          setAddress(response.data.address_str);
        }
      } catch (error) {
        console.error("Error fetching shift status:", error);
      }
    };


    fetchUser();
    checkClockStatus();
    fetchClockInTime();
    fetchFullShiftTime();
  }, [navigate]);

  const handleLogout = async () => {
    await logoutUser(); // Clear tokens
    navigate("/");  // Redirect to the login page
  };

  // Called when the user confirms clock-in from the modal
  const handleConfirmClockIn = async () => {
    // Proceed to clock the user in with the pending location
    try {
      const response = await axiosClient.post("/shifts/clock_in/", {
        latitude: pendingLocation.latitude,
        longitude: pendingLocation.longitude,
        address_clock_in: pendingLocation.address
      });
      if (response.data.status === "success") {
        setIsClockedIn(true);
      } else {
        console.log(response.data.message)
      }
    } catch (error) {
      console.error("Clock in error:", error);
    }

    try {
      const response = await axiosClient.get("/shifts/status/");
      if (response.data.status === "success") {
        setClockInTime(response.data.clock_in_time);
      } else {
        console.log(response.data.message)
      }
    } catch (error) {
      console.error("Error fetching shift status:", error);
    }

    // Set the confirmed address and hide the modal
    setAddress(pendingLocation.address);
    setConfirmModalVisible(false);
    setLoading(false);
  };

  const handleCancelClockIn = () => {
    // User canceled clock-in; hide the modal and stop loading
    setConfirmModalVisible(false);
    setLoading(false);
  };

  const handleClockIn = async () => {
    setLoading(true);
    // Get the user's current location
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        let fetchedAddress = "";
        // Reverse geocode: using OpenStreetMap's Nominatim API
        try {
          const geocodeResponse = await axiosClient.get(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`
          );
          
          // Format the address as needed; here we'll just print the "display_name" from the response.
          if (geocodeResponse.data && geocodeResponse.data.address) {
            fetchedAddress = formatAddress(geocodeResponse.data);
            console.log("Short Address:", fetchedAddress);
          }
        } catch (error) {
          console.error("Reverse geocoding error:", error);
          fetchedAddress = "Address not found";
        }

        // Instead of immediately clocking in, show the confirmation modal
        setLocation({ latitude, longitude, address: fetchedAddress });
        setConfirmModalVisible(true);
      },
      (error) => {
        console.error("Error getting location:", error);
        setLoading(false);
      }
    );
  };

  const handleClockOut = async () => {
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        let fetchedAddress = "";
        // Reverse geocode: using OpenStreetMap's Nominatim API
        try {
          const geocodeResponse = await axiosClient.get(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`
          );
          
          // Format the address as needed; here we'll just print the "display_name" from the response.
          if (geocodeResponse.data && geocodeResponse.data.address) {
            fetchedAddress = formatAddress(geocodeResponse.data);
            console.log("Short Address:", fetchedAddress);
          }
        } catch (error) {
          console.error("Reverse geocoding error:", error);
          fetchedAddress = "Address not found";
        }
        try {
          const response = await axiosClient.post("/shifts/clock_out/", {
            latitude: latitude,
            longitude: longitude,
            address_clock_out: fetchedAddress
          });
          if (response.data.status === "success") {
            setIsClockedIn(false);
          }
        } catch (error) {
          console.error("Clock out error:", error);
        }

        try {
          const response = await axiosClient.get("/shifts/full_shift/");
          if (response.data.status === "success") {
            setClockInTime(response.data.clock_in_time);
            setClockOutTime(response.data.clock_out_time);
            setAddress(response.data.address_str);
          }
        } catch (error) {
          console.error("Error fetching shift status:", error);
        }
      },
      (error) => {
        console.error("Error getting location:", error);
        setLoading(false);
      }
    );
    setLoading(false);
  };



  return (
    <>
      <header className="app-header" style={{ borderBottom: "none", boxShadow: "none" }}>
        <img src="/new_logo.png" alt="Logo" className="logo-dashboard-nav" />
        
        <nav className="header-nav">
          <button onClick={() => navigate("/shifts")} className="home-button">
            My Shifts
          </button>
          <button onClick={handleLogout} className="logout-shifts-button">
            Logout
          </button>
        </nav>
      </header>

    <div className="page-container">
      {user ? (
        <>
          <h1 className="title-dashboard">Welcome, {user.first_name}!</h1>
           {/* Display clock-in status */}
           <div className="subheading" style={{textAlign:'center'}}>
            {/* Show user is clocked in and start time */}
            {isClockedIn ? (
              <>
              <p style={{color:"green", fontWeight: "bold"}}>You are currently clocked in.</p>
              <p style={{fontWeight: "bold", marginTop: '35px'}}> <FaClock className="clock-icon"/> Current Shift</p>
              {address && (
                <p style={{ fontSize: 16 }}>
                <span style={{ color:"grey",fontWeight: 'bold' }}>Location </span>
                <span style={{ marginLeft: '15px' }}>{address}</span>
              </p>
              )}
              {clockInTime && (
                <p style={{ fontSize: 16 }}>
                  <span style={{ color:"grey",fontWeight: 'bold' }}>Started </span>
                  <span style={{ marginLeft: '5px' }}>{clockInTime}</span>
                </p>
              )}
              </>
            ) : (
              <>
              {/* Show user is not clocked in and last shift start/end time */}
              <p style={{color:"red", fontWeight: "bold"}}>You are not clocked in.</p>
              <p style={{fontWeight: "bold", marginTop: '35px'}}> <FaClock className="clock-icon"/> Last Shift</p>
              {address && (
                <p style={{ fontSize: 16 }}>
                <span style={{ color:"grey",fontWeight: 'bold' }}>Location </span>
                <span style={{ marginLeft: '15px' }}>{address}</span>
              </p>
              )}
              {clockInTime && (
                <p style={{ fontSize: 16 }}>
                  <span style={{ color:"grey",fontWeight: 'bold' }}>Started </span>
                  <span style={{ marginLeft: '5px' }}>{clockInTime}</span>
                </p>
              )}
              {clockOutTime && (
                <p style={{ fontSize: 16 }}>
                  <span style={{ color:"grey",fontWeight: 'bold' }}>Ended </span>
                  <span style={{ marginLeft: '5px' }}>{clockOutTime}</span>
                </p>
              )}
              </>
            )}
          </div>
          {/* Clock In / Clock Out Buttons */}
          <div className="clock-buttons">
            <button
              onClick={handleClockIn}
              className={isClockedIn ? "clock-in-out-button-greyed" : "clock-in-out-button"}
              disabled={isClockedIn || loading}
            >
              {loading && !isClockedIn ? "Processing..." : "Clock In"}
            </button>

            <button
              onClick={handleClockOut}
              className={isClockedIn ? "clock-in-out-button" : "clock-in-out-button-greyed"}
              disabled={!isClockedIn || loading}
            >
              {loading && isClockedIn ? "Processing..." : "Clock Out"}
            </button>
          </div>
        </>
      ) : (
        <p>Loading...</p>
      )}

      {/* Confirmation Modal */}
      {confirmModalVisible && (
        <div className="modal">
          <div className="modal-content">
            <h3>Confirm Clock In</h3>
            <p>
              Your approximate location is:
              <br />
              {pendingLocation.address}
            </p>
            <div className="modal-buttons">
              <button onClick={handleConfirmClockIn} className="confirm-button">
                Confirm
              </button>
              <button onClick={handleCancelClockIn} className="cancel-button">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
    </>
  );
};

export default Dashboard;
